// ════════════════════════════════════════════════════════════════════════════
//  subtitles-utils.js
//  Fast client-side SRT to WebVTT conversion and real-time cue parser
// ════════════════════════════════════════════════════════════════════════════

/**
 * Parses timestamp string "00:01:23,456" or "00:01:23.456" into seconds.
 */
export function parseTimestampToSeconds(timeStr) {
  if (!timeStr) return 0;
  const cleaned = timeStr.trim().replace(',', '.');
  const parts = cleaned.split(':');
  if (parts.length === 3) {
    return (
      parseFloat(parts[0]) * 3600 +
      parseFloat(parts[1]) * 60 +
      parseFloat(parts[2])
    );
  } else if (parts.length === 2) {
    return parseFloat(parts[0]) * 60 + parseFloat(parts[1]);
  }
  return parseFloat(cleaned) || 0;
}

/**
 * Parses an entire SRT text file into an array of cues: [{ start, end, text }]
 */
export function parseSrt(srtContent) {
  if (!srtContent || typeof srtContent !== 'string') return [];

  // Normalize line endings
  const normalized = srtContent.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const blocks = normalized.trim().split(/\n\s*\n/);
  const cues = [];

  for (const block of blocks) {
    const lines = block.trim().split('\n');
    if (lines.length < 2) continue;

    // Detect line with arrow "-->"
    let timeIndex = lines.findIndex(l => l.includes('-->'));
    if (timeIndex === -1) continue;

    const timeLine = lines[timeIndex];
    const [startRaw, endRaw] = timeLine.split('-->').map(s => s.trim());
    if (!startRaw || !endRaw) continue;

    const start = parseTimestampToSeconds(startRaw);
    const end = parseTimestampToSeconds(endRaw);
    const text = lines.slice(timeIndex + 1).join('\n').trim();

    if (text) {
      cues.push({ start, end, text });
    }
  }

  return cues;
}

/**
 * Converts SRT text directly into a valid WebVTT Blob URL.
 * WebVTT requires the header "WEBVTT" and periods instead of commas for milliseconds.
 */
export function srtToVttBlobUrl(srtContent) {
  if (!srtContent) return null;
  const vttHeader = "WEBVTT\n\n";
  const converted = srtContent
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/(\d{2}:\d{2}:\d{2}),(\d{3})/g, '$1.$2');

  const blob = new Blob([vttHeader + converted], { type: 'text/vtt' });
  return URL.createObjectURL(blob);
}
